import {history} from 'utils/history'

import Settings from 'Settings'

import {mergeDeepRight, propEq, has} from 'ramda'
import URLParamsMapper from 'components/data/URLParams/URLParamsMapper'

import {
  scrollToElementOnCenter,
  scrollToWindowTop
} from '@daedalus/core/src/utils/dom'
import updateHistoryLocation from 'utils/updateHistoryLocation'
import buildUrl from 'utils/buildUrl'
import urls from 'utils/urls'
import {trimSlashes} from '@daedalus/core/src/utils/url'

import {
  setPageAsRefreshedStatus,
  getPageAsRefreshedStatus
} from 'services/sessionStorage'
import createRiskifiedInstance from 'services/riskified'

import {
  ROOM_SELECTION_PAGE_PATH,
  PAYMENT_PAGE_PATH,
  CONFIRMATION_PAGE_PATH
} from 'routes'

import {
  HISTORY_BACK,
  REFRESH_PAGE,
  REFRESH_PAGE_ONCE,
  CLEAR_REFRESHED_STATUS,
  SCROLL_TO,
  NAVIGATE_TO,
  SCROLL_TO_WINDOW_TOP,
  NAVIGATE_TO_OUTSIDE,
  NAVIGATE_TO_SEARCH,
  NAVIGATE_TO_PLACE_SEARCH,
  NAVIGATE_TO_ROOM_SELECTION,
  NAVIGATE_TO_PAYMENT_PAGE,
  UPDATE_SEARCH_PARAMS,
  RSP_LOADED,
  PP_LOADED,
  navigateTo,
  NAVIGATE_TO_CONFIRMATION_PAGE,
  NavigateToPaymentPageParams
} from 'modules/browser/actions'

import {trackAll} from './analytics'

import {SIGN_UP_TOKEN} from '@daedalus/core/src/offer/business/privateDeals'

import {MiddlewareType} from 'middlewares'
import {SILENT_AUTH_URL_TRIGGER} from '@daedalus/core/src/auth/constants'
import {parse} from 'qs'
import {toggle} from 'opticks/lib/optimizely'
import {offerCheckClear} from 'modules/offerCheck/slice'
import {
  setSelectedOfferCheckoutUrl,
  setSelectedRoomAndOfferPosition
} from 'modules/rooms/slice'

const buildSearchParams = ({
  placeID,
  isAuthenticated,
  isAudience,
  params
}: {
  placeID?: string
  isAuthenticated?: boolean
  isAudience?: boolean
  params?: {
    checkIn?: string
    checkOut?: string
    rooms?: string,
    utm_source?: string,
    utm_campaign?: string,
  }
}) => {
  // eslint-disable-next-line  new-cap
  const {searchWebsiteParams, metaParams} = URLParamsMapper()
  const authenticated = isAuthenticated ? 1 : undefined
  const token = isAudience ? SIGN_UP_TOKEN : undefined
  const {gsite} = metaParams

  const searchWebsiteParamsToBeMerged = {
    ...searchWebsiteParams,
    ...(placeID ? {placeID, hotelId: undefined} : {}),
    ...params
  }

  return mergeDeepRight(searchWebsiteParamsToBeMerged, {
    authenticated,
    token,
    gsite
  })
}

/* eslint-disable complexity */
const middleware: MiddlewareType = store => next => async action => {
  const {dispatch} = store

  next(action)

  switch (action.type) {
    case HISTORY_BACK: {
      history.goBack()
      break
    }

    case REFRESH_PAGE: {
      history.go()
      break
    }

    case REFRESH_PAGE_ONCE: {
      if (!getPageAsRefreshedStatus()) {
        setPageAsRefreshedStatus(true)
        trackAll(`Checkout.forceRefreshPage`)
        window.setTimeout(() => {
          history.go()
        }, 1000)
      }

      break
    }

    case CLEAR_REFRESHED_STATUS: {
      setPageAsRefreshedStatus(false)
      break
    }

    case SCROLL_TO: {
      scrollToElementOnCenter(action.elementRef)
      break
    }

    case SCROLL_TO_WINDOW_TOP: {
      scrollToWindowTop()
      break
    }

    case NAVIGATE_TO_ROOM_SELECTION: {
      const {params} = URLParamsMapper()
      const newSearchParams = mergeDeepRight(params, {
        offerId: undefined,
        roomId: undefined,
        ...action?.params
      })

      dispatch(navigateTo(ROOM_SELECTION_PAGE_PATH, newSearchParams))
      break
    }

    case NAVIGATE_TO_CONFIRMATION_PAGE: {
      dispatch(
        navigateTo(
          CONFIRMATION_PAGE_PATH,
          action.params.bookingParams,
          false,
          true
        )
      )
      break
    }

    case NAVIGATE_TO_PAYMENT_PAGE: {
      const {
        offerParams,
        urlParams,
        isAuthenticated,
        selectedRoomPosition,
        selectedOfferPosition
      } = action.navigateParams as NavigateToPaymentPageParams
      const {offer, roomId} = offerParams

      const {replaceHistory = false} = action

      // sapi only returns checkout link
      const checkoutLink = offer.links.find(propEq('type', 'checkout'))

      // To handle the case user's sign in through Room Selection page
      const shouldAddSSOTrigger =
        isAuthenticated && !has(SILENT_AUTH_URL_TRIGGER)(urlParams)

      const search = parse(new URL(checkoutLink.href).search, {
        ignoreQueryPrefix: true
      })

      const redirectParams = {
        offerId: offer.id,
        providerCode: offer.providerCode,
        roomId,
        ...(shouldAddSSOTrigger && {[SILENT_AUTH_URL_TRIGGER]: 1}),
        ...search
      }

      dispatch(offerCheckClear())

      dispatch(
        setSelectedRoomAndOfferPosition({
          selectedRoomPosition,
          selectedOfferPosition
        })
      )

      dispatch(setSelectedOfferCheckoutUrl(checkoutLink?.href))
      dispatch(
        navigateTo(PAYMENT_PAGE_PATH, redirectParams, false, replaceHistory)
      )
      break
    }

    case NAVIGATE_TO_SEARCH: {
      const {
        isAuthenticated,
        isAudience,
        isNewTab,
        checkIn,
        checkOut,
        rooms,
        utm_source,
        utm_campaign
      } = action.params

      const searchParams = {
        forceCurrencyChange: 1,
        forceLanguageChange: 1,
        ...buildSearchParams({
          isAudience,
          isAuthenticated,
          params: {checkIn, checkOut, rooms, utm_source, utm_campaign}
        })
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      isNewTab
        ? window.open(
            buildUrl(trimSlashes(urls.hotelSearch), searchParams),
            '_blank'
          )
        : window.location.assign(
            buildUrl(trimSlashes(urls.hotelSearch), searchParams)
          )

      break
    }

    case NAVIGATE_TO_PLACE_SEARCH: {
      const {placeID, isAuthenticated, isAudience} = action.params

      const searchParams = {
        forceCurrencyChange: 1,
        forceLanguageChange: 1,
        ...buildSearchParams({
          placeID,
          isAudience,
          isAuthenticated
        })
      }

      window.location.assign(
        buildUrl(trimSlashes(urls.hotelSearch), searchParams)
      )
      break
    }

    case NAVIGATE_TO_OUTSIDE: {
      const {url, search} = action.params
      const {params} = URLParamsMapper()
      const newSearchParams = search ? mergeDeepRight(params, search) : null

      window.location = buildUrl(url, newSearchParams)
      break
    }

    case NAVIGATE_TO: {
      const {url, search, replaceSearchParams, replace} = action.params
      updateHistoryLocation({url, search, replaceSearchParams, replace})
      break
    }

    case UPDATE_SEARCH_PARAMS: {
      const {search, replaceSearchParams} = action.params
      const url = history.location.pathname

      updateHistoryLocation({url, search, replaceSearchParams})
      break
    }

    case RSP_LOADED:
    case PP_LOADED:
      try {
        const {pageView} = await createRiskifiedInstance({
          storeDomain: Settings.get('REACT_APP_RISKIFIED_SHOP_URL')
        })
        pageView(`${window.location.pathname}${window.location.search}`)
      } catch {
        console.log('Riskified::error calling pageView()')
      }

      break

    default:
      break
  }
}

export default middleware
